import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { InfoCardComponent } from "@shared/components/info-card/info-card.component";
import { PopupPasswordTemplateComponent } from "@shared/components/popup-password-template/popup-password-template.component";
import { LOCKEDOUT, REQUIRES_TWO_FACTOR } from "@shared/constants/error-details.constants";
import { CONNECTION_ERROR, CONNECTION_IMPOSSIBLE, LOCKEDOUT_MESSAGE } from "@shared/constants/error-messages.constants";
import { CHECK_SAVE_EMAIL, EMAIL, PASSWORD } from "@shared/constants/form-controls.constants";
import { ACCUEIL, AUTHENTIFICATION_FORTE } from "@shared/constants/route.constants";
import { EMAIL_REGEX, PASSWORD_REQUIREMENTS } from "@shared/constants/validators.constants";
import { AuthenticationService } from "@shared/services/authentication.service";
import { IdleUserService } from "@shared/services/idle-user.service";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { PasswordModule } from "primeng/password";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
@Component({
  selector: "app-login",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    CheckboxModule,
    ButtonModule,
    OverlayPanelModule,
    ToastModule,
    RouterLink,
    TranslateModule,
    PasswordModule,
    TooltipModule,
    PopupPasswordTemplateComponent,
    InfoCardComponent,
  ],
  providers: [MessageService],
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  passwordBlurred = false;
  emailBlurred = false;
  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private idleUserService: IdleUserService,
    private messageService: MessageService
  ) {}

  public formGroup: FormGroup = new FormGroup({
    email: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.pattern(EMAIL_REGEX)],
      nonNullable: true,
    }),
    password: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.minLength(8), Validators.pattern(PASSWORD_REQUIREMENTS)],
      nonNullable: true,
    }),
    checkSaveEmail: new FormControl<boolean>(false),
  });

  get email() {
    return this.formGroup.get(EMAIL);
  }

  get password() {
    return this.formGroup.get(PASSWORD);
  }

  get checkSaveEmail() {
    return this.formGroup.get(CHECK_SAVE_EMAIL);
  }

  loading = false;

  ngOnInit() {
    this.preFillEmail();
  }

  login() {
    this.loading = true;

    this.authenticationService.checkRememberMeToken().subscribe((tokenValue: string) => {
      this.redirectAuthentication(tokenValue);
    });
  }

  displayErrorAndStopLoading(message: string) {
    this.messageService.add({
      severity: "error",
      summary: CONNECTION_ERROR,
      detail: message,
    });
    this.loading = false;
  }

  manageSaveEmail() {
    if (this.checkSaveEmail?.value) {
      localStorage.setItem(EMAIL, this.email?.value);
    } else {
      localStorage.removeItem(EMAIL);
    }
  }

  preFillEmail() {
    const savedEmail = localStorage.getItem(EMAIL)?.trim();
    if (savedEmail) {
      this.email?.setValue(savedEmail);
      this.checkSaveEmail?.setValue(true);
    }
  }

  redirectAuthentication(tokenValue: string) {
    if (tokenValue !== "") {
      this.proceedWithTokenLogin(tokenValue);
    } else {
      this.proceedWithEmptyTokenLogin();
    }
  }

  private proceedWithTokenLogin(tokenValue: string) {
    this.authenticationService.sendLogin(this.email?.value, this.password?.value, tokenValue, false).subscribe({
      next: () => {
        this.idleUserService.setUserIdle();
        this.manageSaveEmail();
        this.router.navigate([ACCUEIL]);
      },
      error: error => {
        this.proceedWithEmptyTokenLogin();
      },
    });
  }

  private proceedWithEmptyTokenLogin() {
    this.authenticationService.sendLogin(this.email?.value, this.password?.value, "", false).subscribe({
      next: () => {
        this.idleUserService.setUserIdle();
      },
      error: error => {
        this.loading = false;
        if (error.error.status === 401 && error.error.detail === LOCKEDOUT) {
          this.displayErrorAndStopLoading(LOCKEDOUT_MESSAGE);
        }
        if (error.error.status === 401 && error.error.detail === REQUIRES_TWO_FACTOR) {
          this.authenticationService.sendToken(this.email?.value).subscribe({
            next: () => {
              this.authenticationService.sharedEmail(this.email?.value);
              this.authenticationService.sharedPassword(this.password?.value);
              this.manageSaveEmail();
              this.router.navigate([AUTHENTIFICATION_FORTE]);
            },
            error: error => {
              this.displayErrorAndStopLoading(CONNECTION_IMPOSSIBLE);
              return error;
            },
          });
        } else {
          this.displayErrorAndStopLoading(CONNECTION_IMPOSSIBLE);
          return error;
        }
      },
    });
  }
}
